import { Card, Button, Accordion, AccordionContent, AccordionPanel, AccordionTitle } from "flowbite-react";
import { useNavigate } from "react-router-dom";
import { useTranslation, useAuthentication, getANPHost } from "@repo/core";
import {
    HiChartBar,
    HiCog,
    HiTrendingUp,
    HiCalculator,
    HiCurrencyDollar,
    HiDocumentText,
} from "react-icons/hi";

const Welcome = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuthentication();
    const host = getANPHost();

    return (
        <div className="container mx-auto px-4 py-12 max-w-7xl">
            {/* Section 1: Next-Gen Investing */}
            <div className="mb-16" data-tour="welcome-section-1">
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 text-center mb-8">
                    {t("welcome.nextGen.subtitle")}
                </h2>
                <div className="flex justify-center gap-6">
                    <div className="w-full md:w-1/3">
                        {/* Card 1 */}
                        <Card className="text-center flex flex-col h-full">
                            <div className="flex justify-center mb-4">
                                <HiChartBar className="w-16 h-16 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-bold mb-2">
                                {t("welcome.nextGen.card1.title")}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                                {t("welcome.nextGen.card1.description")}
                            </p>
                            {user?.permissions?.includes("Factor_investing_basic") ? (
                                <Button
                                    onClick={() => navigate("/")}
                                    color="blue"
                                    className="cursor-pointer mt-auto"
                                >
                                    {t("welcome.nextGen.card1.button")}
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => navigate("/subscriptions")}
                                    color="blue"
                                    className="cursor-pointer mt-auto"
                                >
                                    Subscribe Now
                                </Button>
                            )}
                        </Card>
                    </div>

                    {/* Card 3 */}
                    <div className="w-full md:w-1/3">
                        <Card className="text-center flex flex-col h-full">
                            <div className="flex justify-center mb-4">
                                <HiCog className="w-16 h-16 text-purple-600" />
                            </div>
                            <h3 className="text-xl font-bold mb-2">
                                {t("welcome.nextGen.card3.title")}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                                {t("welcome.nextGen.card3.description")}
                            </p>
                            {user?.permissions?.includes("Stockpicker") ? (
                                <Button
                                    onClick={() => window.location.href = `${host}/stockPickingLab`}
                                    color="purple"
                                    className="cursor-pointer mt-auto"
                                >
                                    {t("welcome.nextGen.card3.button")}
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => navigate("/subscriptions")}
                                    color="purple"
                                    className="cursor-pointer mt-auto"
                                >
                                    Subscribe Now
                                </Button>
                            )}
                        </Card>
                    </div>

                </div>
            </div>

            {/* Section 2: Investment Theories */}
            <div className="mb-16" data-tour="welcome-section-2">
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 text-center mb-8">
                    {t("welcome.theories.subtitle")}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Card 1 */}
                    <Card className="text-center flex flex-col h-full">
                        <div className="flex justify-center mb-4">
                            <HiTrendingUp className="w-16 h-16 text-green-600" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">
                            {t("welcome.theories.card1.title")}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                            {t("welcome.theories.card1.description")}
                        </p>
                        {user?.permissions?.includes("SFA_articles") ? (
                            <Button
                                onClick={() => window.location.href = "https://sfa.analyticalplatform.com/#/feed"}
                                color="lime"
                                className="cursor-pointer mt-auto"
                            >
                                {t("welcome.theories.card1.button")}
                            </Button>
                        ) : (
                            <Button
                                onClick={() => navigate("/subscriptions")}
                                color="lime"
                                className="cursor-pointer mt-auto"
                            >
                                Subscribe Now
                            </Button>
                        )}
                    </Card>

                    {/* Card 2 */}
                    <Card className="text-center flex flex-col h-full">
                        <div className="flex justify-center mb-4">
                            <HiCalculator className="w-16 h-16 text-indigo-600" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">
                            {t("welcome.theories.card2.title")}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                            {t("welcome.theories.card2.description")}
                        </p>
                        {user?.permissions?.includes("Portfolio_optimization") ? (
                            <Button
                                onClick={() => window.location.href = `${host}/optimization-fe/portfolioManager`}
                                color="indigo"
                                className="cursor-pointer mt-auto"
                            >
                                {t("welcome.theories.card2.button")}
                            </Button>
                        ) : (
                            <Button
                                onClick={() => navigate("/subscriptions")}
                                color="indigo"
                                className="cursor-pointer mt-auto"
                            >
                                Subscribe Now
                            </Button>
                        )}
                    </Card>

                    {/* Card 3 */}
                    <Card className="text-center flex flex-col h-full">
                        <div className="flex justify-center mb-4">
                            <HiCurrencyDollar className="w-16 h-16 text-red-600" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">
                            {t("welcome.theories.card3.title")}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                            {t("welcome.theories.card3.description")}
                        </p>
                        {user?.permissions?.includes("Pattern_lab") ? (
                            <Button
                                onClick={() => window.location.href = `${host}/patternLab`}
                                color="red"
                                className="cursor-pointer mt-auto"
                            >
                                {t("welcome.theories.card3.button")}
                            </Button>
                        ) : (
                            <Button
                                onClick={() => navigate("/subscriptions")}
                                color="red"
                                className="cursor-pointer mt-auto"
                            >
                                Subscribe Now
                            </Button>
                        )}
                    </Card>
                </div>
            </div>

            {/* Section 3: Automated Taxes */}
            <div className="mb-16">
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 text-center mb-8">
                    {t("welcome.taxes.subtitle")}
                </h2>
                <div className="flex justify-center">
                    <div className="w-full md:w-1/3">
                        <Card className="text-center flex flex-col h-full">
                            <div className="flex justify-center mb-4">
                                <HiDocumentText className="w-16 h-16 text-teal-600" />
                            </div>
                            <h3 className="text-xl font-bold mb-2">
                                {t("welcome.taxes.card.title")}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                                {t("welcome.taxes.card.description")}
                            </p>
                            {user?.permissions?.includes("Tax_app") ? (
                                <Button
                                    onClick={() => window.location.href = `${host}/taxes`}
                                    color="teal"
                                    className="cursor-pointer mt-auto"
                                >
                                    {t("welcome.taxes.card.button")}
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => navigate("/subscriptions")}
                                    color="teal"
                                    className="cursor-pointer mt-auto"
                                >
                                    Subscribe Now with any Plan
                                </Button>
                            )}
                        </Card>
                    </div>
                </div>
            </div>

            {/* Section 4: FAQ */}
            <div className="mb-16" data-tour="welcome-faq">
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 text-center mb-8">
                    {t("welcome.faq.subtitle")}
                </h2>
                <Accordion className="max-w-4xl mx-auto">
                    <AccordionPanel>
                        <AccordionTitle className="cursor-pointer bg-white dark:bg-gray-800">
                            {t("welcome.faq.question1.title")}
                        </AccordionTitle>
                        <AccordionContent>
                            <p className="text-gray-600 dark:text-gray-400">
                                {t("welcome.faq.question1.answer")}
                            </p>
                        </AccordionContent>
                    </AccordionPanel>
                    <AccordionPanel>
                        <AccordionTitle className="cursor-pointer bg-white dark:bg-gray-800">
                            {t("welcome.faq.question2.title")}
                        </AccordionTitle>
                        <AccordionContent>
                            <p className="text-gray-600 dark:text-gray-400">
                                {t("welcome.faq.question2.answer")}
                            </p>
                        </AccordionContent>
                    </AccordionPanel>
                    <AccordionPanel>
                        <AccordionTitle className="cursor-pointer bg-white dark:bg-gray-800">
                            {t("welcome.faq.question3.title")}
                        </AccordionTitle>
                        <AccordionContent>
                            <p className="text-gray-600 dark:text-gray-400">
                                {t("welcome.faq.question3.answer")}
                            </p>
                        </AccordionContent>
                    </AccordionPanel>
                    <AccordionPanel>
                        <AccordionTitle className="cursor-pointer bg-white dark:bg-gray-800">
                            {t("welcome.faq.question4.title")}
                        </AccordionTitle>
                        <AccordionContent>
                            <p className="text-gray-600 dark:text-gray-400">
                                {t("welcome.faq.question4.answer")}
                            </p>
                        </AccordionContent>
                    </AccordionPanel>
                    <AccordionPanel>
                        <AccordionTitle className="cursor-pointer bg-white dark:bg-gray-800">
                            {t("welcome.faq.question5.title")}
                        </AccordionTitle>
                        <AccordionContent>
                            <p className="text-gray-600 dark:text-gray-400">
                                {t("welcome.faq.question5.answer")}
                            </p>
                        </AccordionContent>
                    </AccordionPanel>
                    <AccordionPanel>
                        <AccordionTitle className="cursor-pointer bg-white dark:bg-gray-800">
                            {t("welcome.faq.question6.title")}
                        </AccordionTitle>
                        <AccordionContent>
                            <p className="text-gray-600 dark:text-gray-400">
                                {t("welcome.faq.question6.answer")}
                            </p>
                        </AccordionContent>
                    </AccordionPanel>
                    <AccordionPanel>
                        <AccordionTitle className="cursor-pointer bg-white dark:bg-gray-800">
                            {t("welcome.faq.question7.title")}
                        </AccordionTitle>
                        <AccordionContent>
                            <p className="text-gray-600 dark:text-gray-400">
                                {t("welcome.faq.question7.answer")}
                            </p>
                        </AccordionContent>
                    </AccordionPanel>
                </Accordion>
            </div>
        </div>
    );
};

export default Welcome;
